/**
 * ThreadSwitcherBar
 *
 * Slack-style thread/project chip row rendered along the bottom of the
 * GlobalChatSystem chat area (in normal document flow, between the
 * messages list and the composer), so it never overlaps messages.
 *
 * Users can:
 *   • Tap a chip to switch threads (each thread has an isolated message array)
 *   • Tap the "+" button to start a brand-new thread/project
 *   • See a UNIFORM spinner + ticking elapsed clock on every chip whose
 *     thread is currently processing — active thread included (owner
 *     directive, Miles 2026-07-14: a processing thread's summary must read
 *     the same whether or not the user has it open; supersedes the
 *     2026-07-12 "pane-owns-active" rule). OWNER OVERRIDE (Miles
 *     2026-08-01): this holds even while the active thread's pane
 *     pending-overlay is on screen — the chip is the at-a-glance status
 *     surface regardless of where the user is, so the pane clock AND the
 *     chip clock coexist by design (supersedes the 2026-07-15
 *     "pane-owns-active clock" refinement).
 *   • See a red unread-count badge on chips with unread messages
 *   • Scroll horizontally through chips when there are many
 *   • Hover/focus a chip to reveal a tiny × that *requests* delete
 *     (confirmation modal is required — this component never
 *     deletes directly, so accidental deletes are extremely hard)
 *
 * The bar is gated by the parent GlobalChatSystem against internal Prest0n
 * and prest0-vm instances — see `INTERNAL_PREST0N_SOURCES` / the
 * `enableThreadSwitcher` config option.
 */

import { isRoutineStopped } from './routineStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ThreadSwitcherScroll,
  ThreadChipButton,
  ThreadChipLabel,
  ThreadChipSpinner,
  ThreadChipTurnIndicator,
  ThreadUnreadBadge,
  ThreadChipCloseButton,
  ThreadChipPinButton,
  ThreadChipTooltip,
  ThreadChipTooltipMeta,
  ThreadNewButton,
  ThreadOverflowIndicator,
} from './styles';
import type { ChatThread } from './types';
import { ARCHIVE_ALWAYS_AVAILABLE, hasUnseenActivity, orderThreads } from './useChatThreads';
import { effectiveUnreadCount } from './threadArrival';
import { formatElapsedTurnDurationCompact } from '../../services/prest0nSession/progress';
import { RoutineGlyph, routineThreadDisplayName } from './RoutineGlyph';
import { ThreadTypeGlyph } from './ThreadTypeGlyph';
import {
  threadDisplayKindFor,
  threadDisplayTitleFor,
  type ThreadListIdentityProps,
} from './threadListIdentity';
import { isGroupParticipantCount } from '../../services/groupThreads/senderIdentity';
import { resolveScrollBehavior } from '../../utils/scrollMotion';

/**
 * Compact relative time for thread meta ("now", "5m", "3h", "2d"). Shared by
 * the sidebar row meta AND the chip tooltip meta so the two surfaces can
 * never disagree about format. (Moved here from ThreadSidebar 2026-07-13 —
 * ThreadSidebar already imports from this module, so this direction keeps
 * the dependency acyclic; ThreadSidebar re-exports it for existing callers.)
 */
export function relativeActivityLabel(iso: string, nowMs: number = Date.now()): string {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const diff = Math.max(0, nowMs - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

/**
 * Spinner + ticking compact elapsed clock (0:07) rendered INSIDE a chip /
 * sidebar row whose thread has an in-flight turn. UNIFORM in-summary activity
 * indicator (owner directive, Miles 2026-07-14 — supersedes the 2026-07-12
 * "pane-owns-active" rule): EVERY processing thread's summary shows this one
 * indicator, active thread included, driven solely by
 * `processingByThread[thread.id]` (+ `turnStartedAtByThread` for the clock).
 * OWNER OVERRIDE (Miles 2026-08-01): "active thread included" now holds
 * UNCONDITIONALLY — the active chip/row keeps this indicator even while the
 * pane pending-overlay shows its own "working · 0:07". TWO clocks per
 * processing active thread (pane + summary row) are INTENTIONAL under the
 * new contract: the row is the at-a-glance status surface regardless of
 * where the user is. (Supersedes the 2026-07-15 "exactly one elapsed clock
 * per thread" pane-owns-active refinement.)
 * Grew out of the 2026-07-13 active-only fallback (phantom-chip fix); the
 * old plain background-chip spinner merged into this same component so a
 * chip/row can never double-spin. Ticks with its own 1s interval so the huge
 * parent chat component never re-renders per second. Shared by both thread
 * surfaces so they can never disagree. `aria-hidden` — the host chip/row
 * announces the state via its aria-label instead.
 */
export const ThreadTurnIndicator: React.FC<{ sinceMs?: number | null }> = ({ sinceMs }) => {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsedMs = typeof sinceMs === 'number' && sinceMs > 0
    ? Math.max(0, nowMs - sinceMs)
    : 0;
  return (
    <ThreadChipTurnIndicator aria-hidden="true" data-thread-spinner-active="">
      <ThreadChipSpinner />
      {formatElapsedTurnDurationCompact(elapsedMs)}
    </ThreadChipTurnIndicator>
  );
};

/**
 * Pure decision: given a chip close-click on `threadId`, the active
 * thread, the current set of thread records, and (for the active
 * thread) the live `state.messages.length`, determine whether the
 * close click should be routed to the one-click empty-delete path
 * (`onDeleteEmptyThread`) or the legacy confirmation-modal path
 * (`onRequestDelete`).
 *
 * Exported for unit testing without rendering React. The component
 * uses this same predicate inside `handleCloseClick`.
 */
export function shouldRouteCloseToEmptyDelete(args: {
  threadId: string;
  activeThreadId: string | null;
  threads: ReadonlyArray<Pick<ChatThread, 'id' | 'messageCount' | 'hasUserContent'>>;
  /**
   * Live USER-message count for the active thread, when known (the parent
   * passes `state.messages.filter(m => m.isUser).length`). USER messages
   * only: a greeting-seeded draft renders one assistant message yet holds
   * nothing of the user's — deleting it must stay one-click.
   */
  activeThreadMessageCount?: number;
  /** True when the parent has wired the empty-delete callback. */
  hasEmptyDeleteHandler: boolean;
}): boolean {
  if (!args.hasEmptyDeleteHandler) return false;
  // Active thread → trust the live user-message count (synchronously fresh).
  if (args.threadId === args.activeThreadId && typeof args.activeThreadMessageCount === 'number') {
    return args.activeThreadMessageCount === 0;
  }
  // Inactive thread → trust the record: the stamped `hasUserContent` flag
  // when present (greeting-seeded drafts have messageCount > 0 with zero
  // user content and stay one-click deletable); legacy records fall back to
  // the persisted messageCount, exactly as before.
  const target = args.threads.find(t => t.id === args.threadId);
  if (!target) return true;
  if (target.hasUserContent !== undefined) return target.hasUserContent !== true;
  return (target.messageCount || 0) === 0;
}

/**
 * T4 (thread-workflow UX) — pure overflow classification for the chip strip.
 * Given each chip's horizontal extent and the viewport window, counts the
 * chips fully hidden on each side and whether any hidden chip is "hot"
 * (unread or processing). Pure so the math is unit-testable without layout.
 */
export function computeChipOverflow(args: {
  chips: ReadonlyArray<{ id: string; start: number; end: number }>;
  viewStart: number;
  viewEnd: number;
  hotIds: ReadonlySet<string>;
  /** Tolerance so a chip 1px off-screen doesn't flicker the indicator. */
  epsilon?: number;
}): { left: number; right: number; leftHot: boolean; rightHot: boolean } {
  const eps = args.epsilon ?? 4;
  let left = 0;
  let right = 0;
  let leftHot = false;
  let rightHot = false;
  for (const chip of args.chips) {
    if (chip.end <= args.viewStart + eps) {
      left += 1;
      if (args.hotIds.has(chip.id)) leftHot = true;
    } else if (chip.start >= args.viewEnd - eps) {
      right += 1;
      if (args.hotIds.has(chip.id)) rightHot = true;
    }
  }
  return { left, right, leftHot, rightHot };
}

export interface ThreadSwitcherBarProps extends ThreadListIdentityProps {
  /** All threads for this chat instance */
  threads: ChatThread[];
  /** Currently-active thread id */
  activeThreadId: string | null;
  /**
   * DEPRECATED (S5 pane-owns-active, Miles 2026-07-12): superseded twice —
   * the per-thread map below is the sole spinner source of truth. Accepted
   * (and ignored) so existing call sites and tests keep compiling; remove on
   * the next breaking pass.
   */
  isActiveProcessing: boolean;
  /**
   * Per-thread processing flag map — the ONE driver of the in-summary
   * activity indicator (owner directive, Miles 2026-07-14, superseding the
   * 2026-07-12 "pane-owns-active" rule). EVERY chip whose thread id is keyed
   * true — active thread included — renders the uniform spinner + elapsed
   * clock, so a thread's summary always says "working" while its turn runs,
   * whether or not the user has it open. Thread-isolation still holds: a
   * request on thread A never triggers thread B's spinner and
   * stopProcessing(A) never clears thread B's spinner.
   */
  processingByThread?: Record<string, boolean>;
  /**
   * Map<threadId, turnStartedAtMs> — the per-thread CLOCK epoch
   * (`state.turnStartedAtByThread`, stamped once on each turn's rising edge;
   * see threadIsolation.ts). Drives the ticking elapsed clock inside every
   * processing chip's indicator. Missing entry renders 0:00.
   */
  turnStartedAtByThread?: Record<string, number>;
  /**
   * DEPRECATED — accepted and IGNORED (owner override, Miles 2026-08-01).
   *
   * History: the 2026-07-15 "pane-owns-active clock" rule made the ACTIVE
   * chip suppress its spinner + elapsed clock while the pane
   * pending-overlay ("working · 0:07") was on screen, so a processing
   * thread showed exactly one clock. OWNER OVERRIDE (2026-08-01): the
   * summary row must show the working indicator in ALL views/modes
   * INCLUDING the active thread while the pane overlay is visible — the
   * row is the at-a-glance status surface regardless of where the user is,
   * and two clocks (pane + row) are now intentional. The suppression logic
   * is gone; this prop is kept (and ignored) so existing call sites and
   * tests keep compiling — same treatment as `isActiveProcessing`. Remove
   * on the next breaking pass.
   */
  activePaneOverlayVisible?: boolean;
  /**
   * Live USER-message count for the ACTIVE thread (i.e.
   * `state.messages.filter(m => m.isUser).length`). The thread record's own
   * fields are updated by an effect that lags one render commit behind
   * dispatches in the same tick, so for the active thread we prefer
   * this synchronously-fresh signal when deciding whether the thread
   * is empty (used by the one-click empty-delete path).
   *
   * USER messages only (personalized greeting, 2026-07-13): a freshly
   * minted thread may open with a seeded assistant greeting — one pane
   * message containing nothing of the user's — and must remain one-click
   * deletable like any other empty draft.
   *
   * Inactive threads use their record instead (stamped `hasUserContent`
   * when present, legacy `messageCount` otherwise) since live state only
   * reflects the active thread. Optional — omit for legacy callers without
   * one-click empty delete. (Name kept for call-site compatibility.)
   */
  activeThreadMessageCount?: number;
  /** Callback when user clicks an existing chip */
  onSwitch: (threadId: string) => void;
  /** Callback when user clicks the "+" new-thread button */
  onNew: () => void;
  /**
   * Callback when the user *requests* to delete a thread (clicks the × on
   * a chip). Parent is expected to open a confirmation modal; this bar
   * never mutates state directly for deletes, matching the safety rule
   * "don't make it easy to delete the thread."
   */
  onRequestDelete: (threadId: string) => void;
  /**
   * Optional callback for one-click delete of EMPTY threads (zero
   * messages). When provided, clicking × on a chip whose thread has
   * no messages skips the confirmation modal entirely and calls this
   * directly. Non-empty threads still go through `onRequestDelete`
   * → confirmation modal.
   *
   * Rationale (Miles 2026-05-10): "a blank empty thread should be a
   * one-click delete as there's nothing in the thread". Friction on
   * confirming the deletion of nothing is pure UX cost.
   *
   * When undefined, every × click goes through `onRequestDelete`
   * regardless of message count (legacy behavior).
   */
  onDeleteEmptyThread?: (threadId: string) => void;
  /**
   * T1: toggle a thread's pinned state. When provided, chips show a pin
   * affordance (hover, like the ×); pinned chips render a persistent pin
   * marker and order first.
   */
  onTogglePin?: (threadId: string) => void;
  /**
   * threadId → participant count (round 3, 2026-08-01) — the same
   * server-synced map the sidebar sections and the header's "Just you" line
   * read (see ThreadSidebarProps.participantsByThread). Chips are compact, so
   * ONLY group threads wear their two-people glyph here (a chat-bubble on
   * every individual chip would crowd the strip; the sidebar is the primary
   * organizational surface). Routine chips keep RoutineGlyph — one marker per
   * chip, same routines > group precedence as the sidebar.
   */
  participantsByThread?: Record<string, number>;
  /**
   * Docked thread-list round (2026-08-24): reports whether ANY chip is
   * currently hidden off either end of the scroll rail — i.e. "more threads
   * than fit". Derived from the SAME rAF-coalesced computeChipOverflow
   * geometry the ‹/› indicator pills use, so the host's vertical-list
   * trigger and the strip's own affordances can never disagree. Reports
   * false on unmount so a retired strip never strands the trigger visible.
   */
  onOverflowChange?: (hasHiddenThreads: boolean) => void;
  /**
   * The viewer's uid (per-thread notification badges, 2026-09-08): feeds the
   * shared `hasUnseenActivity` predicate so a chip wears a DOT when someone
   * ELSE was active in that thread since the viewer last opened it, and never
   * for the viewer's own activity. Omitted → the dot rests on the local
   * unread count alone.
   */
  viewerUid?: string | null;
}

export const ThreadSwitcherBar: React.FC<ThreadSwitcherBarProps> = ({
  threads,
  activeThreadId,
  processingByThread,
  turnStartedAtByThread,
  activeThreadMessageCount,
  onSwitch,
  onNew,
  onRequestDelete,
  onDeleteEmptyThread,
  onTogglePin,
  participantsByThread,
  onOverflowChange,
  viewerUid,
  participantIdsByThread,
  memberLabels,
  categories,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // PR-5 (2026-09-09): the identity facts the chip name + glyph read — the
  // same bundle the rail and the sheet read (threadListIdentity).
  const listIdentity: ThreadListIdentityProps = {
    participantIdsByThread,
    memberLabels,
    categories,
  };
  const [archiveInFlightIds, setArchiveInFlightIds] = useState<Set<string>>(new Set());

  // Chip tooltip — body-portaled (docked-mode UX round, Miles 2026-07-13).
  // The old CSS-only tooltip lived inside the scroll rail and was clipped
  // into invisibility by the rail's overflow (see ThreadChipTooltip in
  // styles.ts). State-driven show/hide + fixed viewport-clamped coordinates
  // keep it visible in every mode, including the 420px docked pane.
  const [chipTooltip, setChipTooltip] = useState<{
    threadId: string;
    text: string;
    meta: string;
    x: number;
    bottom: number;
  } | null>(null);

  const showChipTooltip = useCallback((
    target: HTMLElement,
    threadId: string,
    text: string,
    meta: string,
  ) => {
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    // Clamp the anchor centre so the (max 280px wide, translateX(-50%))
    // tooltip keeps an 8px margin inside the viewport — centered on the
    // first/last chip it would otherwise extend past the pane/screen edge.
    const halfWidth = 148; // 280px max-width / 2 + 8px margin
    const x = Math.min(
      Math.max(rect.left + rect.width / 2, halfWidth),
      Math.max(viewportWidth - halfWidth, halfWidth),
    );
    const bottom = Math.max((window.innerHeight || 0) - rect.top + 8, 0);
    setChipTooltip({ threadId, text, meta, x, bottom });
  }, []);

  const hideChipTooltip = useCallback(() => setChipTooltip(null), []);

  // Never leave a tooltip up for a chip that just left the strip (an archive
  // completing mid-hover unmounts the chip without firing mouseleave).
  useEffect(() => {
    setChipTooltip((prev) => (
      prev && !threads.some((t) => t.id === prev.threadId) ? null : prev
    ));
  }, [threads]);

  useEffect(() => {
    setArchiveInFlightIds((prev) => {
      let changed = false;
      const liveIds = new Set(threads.map((thread) => thread.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (liveIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [threads]);

  // T4: overflow indicators — the strip's scrollbar is hidden, so hidden
  // chips need explicit signals. Geometry is read from the DOM; the counting
  // is the pure computeChipOverflow above.
  const [overflow, setOverflow] = useState<{ left: number; right: number; leftHot: boolean; rightHot: boolean }>(
    { left: 0, right: 0, leftHot: false, rightHot: false },
  );
  const overflowRafRef = useRef<number | null>(null);
  // Latest-compute ref: the rAF callback and scroll listener are bound once,
  // so they must dereference the CURRENT computation (fresh threads/flags),
  // never a stale closure.
  const recomputeNowRef = useRef<() => void>(() => undefined);
  const recomputeOverflow = useCallback(() => {
    if (overflowRafRef.current !== null) return; // coalesce to one per frame
    if (typeof requestAnimationFrame === 'function') {
      overflowRafRef.current = requestAnimationFrame(() => {
        overflowRafRef.current = null;
        recomputeNowRef.current();
      });
    } else {
      recomputeNowRef.current();
    }
  }, []);
  const recomputeOverflowNow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const chipEls = Array.from(el.querySelectorAll<HTMLElement>('[data-thread-id]'));
    const hotIds = new Set<string>();
    for (const t of threads) {
      const processing = processingByThread ? !!processingByThread[t.id] : false;
      if (effectiveUnreadCount(t) > 0 || processing) hotIds.add(t.id);
    }
    const next = computeChipOverflow({
      chips: chipEls.map((c) => ({
        id: c.dataset.threadId || '',
        start: c.offsetLeft,
        end: c.offsetLeft + c.offsetWidth,
      })),
      viewStart: el.scrollLeft,
      viewEnd: el.scrollLeft + el.clientWidth,
      hotIds,
    });
    setOverflow((prev) => (
      prev.left === next.left && prev.right === next.right
        && prev.leftHot === next.leftHot && prev.rightHot === next.rightHot
        ? prev
        : next
    ));
  }, [threads, processingByThread]);

  useEffect(() => {
    recomputeNowRef.current = recomputeOverflowNow;
    recomputeOverflowNow();
  }, [recomputeOverflowNow]);

  useEffect(() => () => {
    if (overflowRafRef.current !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(overflowRafRef.current);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => recomputeOverflow();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Rail-width changes that arrive WITHOUT a window resize must also
    // recount hidden chips (docked thread-list round, 2026-08-24): the ☰
    // trigger mounting/unmounting beside the rail, the pane's width morph
    // between modes, density-var flips. Guarded — jsdom has no
    // ResizeObserver, and the rAF-coalesced recompute absorbs the bursts.
    let railResizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver === 'function') {
      railResizeObserver = new ResizeObserver(onScroll);
      railResizeObserver.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      railResizeObserver?.disconnect();
    };
  }, [recomputeOverflow]);

  // Docked thread-list round (2026-08-24): surface the ONE bit the host
  // cares about — "some threads don't fit" — whenever the measured overflow
  // state changes; false again on unmount (strip suppression, mode change)
  // so the host's trigger can never outlive the strip it serves. Latest-ref
  // idiom (same as recomputeNowRef above) keeps the unmount report correct
  // without re-running the effect when the callback identity changes.
  const onOverflowChangeRef = useRef(onOverflowChange);
  useEffect(() => {
    onOverflowChangeRef.current = onOverflowChange;
  }, [onOverflowChange]);
  const hasHiddenThreads = overflow.left + overflow.right > 0;
  useEffect(() => {
    onOverflowChangeRef.current?.(hasHiddenThreads);
  }, [hasHiddenThreads]);
  useEffect(() => () => {
    onOverflowChangeRef.current?.(false);
  }, []);

  const pageScroll = useCallback((dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft += dir * Math.max(el.clientWidth * 0.8, 120);
  }, []);

  // T4: one-time completion pulse — when a thread's processing flag flips
  // true→false, its chip pulses briefly IN PLACE (T1 guarantees no reorder),
  // so the eye finds the finished work where it always was.
  const prevProcessingRef = useRef<Record<string, boolean>>({});
  const [pulsingIds, setPulsingIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const prev = prevProcessingRef.current;
    const curr = processingByThread || {};
    const completed: string[] = [];
    for (const id of Object.keys(prev)) {
      if (prev[id] === true && curr[id] !== true) completed.push(id);
    }
    prevProcessingRef.current = { ...curr };
    if (completed.length === 0) return;
    setPulsingIds((old) => new Set([...old, ...completed]));
    const timer = window.setTimeout(() => {
      setPulsingIds((old) => {
        const next = new Set(old);
        completed.forEach((id) => next.delete(id));
        return next;
      });
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [processingByThread]);

  // Keep the active chip in view when it changes — useful when the active
  // thread is deep in the list after many parallel projects.
  useEffect(() => {
    if (!scrollRef.current || !activeThreadId) return;
    // CSS.escape is missing in some embedded WebViews (and jsdom); an unguarded
    // call here crashes the effect and unmounts the whole chip bar. Thread ids
    // are generated (t_<uuid>) so a quote-escape fallback is fully safe.
    const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(activeThreadId)
      : activeThreadId.replace(/["\\]/g, '\\$&');
    const el = scrollRef.current.querySelector<HTMLElement>(
      `[data-thread-id="${escaped}"]`
    );
    if (!el) return;
    // Axis-locked reveal (2026-08 reading-comfort round). scrollIntoView with
    // block:'nearest' scrolls EVERY scrollable ancestor that needs to move,
    // not just this rail — a thread switch could nudge the surface behind it
    // while the reader was mid-message. Nudging the rail's own scrollLeft
    // touches exactly one axis of exactly one element, and only when the chip
    // is genuinely out of view.
    const rail = scrollRef.current;
    const railRect = rail.getBoundingClientRect();
    const chipRect = el.getBoundingClientRect();
    const pad = 8;
    let delta = 0;
    if (chipRect.left < railRect.left + pad) {
      delta = chipRect.left - railRect.left - pad;
    } else if (chipRect.right > railRect.right - pad) {
      delta = chipRect.right - railRect.right + pad;
    }
    if (delta === 0) return;
    const nextLeft = Math.max(0, rail.scrollLeft + delta);
    if (typeof rail.scrollTo === 'function') {
      rail.scrollTo({ left: nextLeft, behavior: resolveScrollBehavior('smooth') });
    } else {
      rail.scrollLeft = nextLeft;
    }
  }, [activeThreadId]);

  const handleChipClick = useCallback((threadId: string) => {
    if (threadId !== activeThreadId) onSwitch(threadId);
  }, [activeThreadId, onSwitch]);

  const handleCloseClick = useCallback((e: React.MouseEvent, threadId: string) => {
    // Stop the chip's click from firing "switch" when the user was really
    // aiming for the tiny × affordance.
    e.stopPropagation();
    e.preventDefault();
    if (archiveInFlightIds.has(threadId)) return;
    // Empty-thread one-click delete (Miles 2026-05-10):
    // Route to the immediate-delete callback when the chip's thread
    // is empty and the parent has wired one. Non-empty threads
    // continue to flow through `onRequestDelete` → confirmation
    // modal, preserving the "don't make it easy to delete" rule.
    const routeToEmptyDelete = shouldRouteCloseToEmptyDelete({
      threadId,
      activeThreadId,
      threads,
      activeThreadMessageCount,
      hasEmptyDeleteHandler: !!onDeleteEmptyThread,
    });
    if (routeToEmptyDelete && onDeleteEmptyThread) {
      // The archive mutates immediately on this path, so the in-flight guard
      // must arm to swallow double-clicks until the chip leaves the list.
      setArchiveInFlightIds((prev) => {
        const next = new Set(prev);
        next.add(threadId);
        return next;
      });
      onDeleteEmptyThread(threadId);
      return;
    }
    // Confirmation-modal path: do NOT arm the in-flight guard here. The modal
    // overlay already prevents double-fire, and arming it broke the × forever
    // when the user clicked "Keep" — the id only leaves the set when the
    // thread leaves the list, which a cancelled archive never does (the
    // "dead ×" bug: × silently ignored every later click on that chip).
    onRequestDelete(threadId);
  }, [archiveInFlightIds, onRequestDelete, onDeleteEmptyThread, threads, activeThreadId, activeThreadMessageCount]);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    // Convert vertical wheel gestures into horizontal scroll for desktop users.
    if (!scrollRef.current) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  // Navigability (2026-07-13): arrow keys move focus along the tablist so a
  // keyboard user can scan chips without tabbing through every chip in turn.
  // Focus only — activation stays native (Enter/Space on the focused chip),
  // matching the "click to switch" behavior exactly.
  const handleTablistKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const el = scrollRef.current;
    if (!el) return;
    const chips = Array.from(el.querySelectorAll<HTMLElement>('[data-thread-id]'));
    const current = chips.indexOf(document.activeElement as HTMLElement);
    if (current === -1) return;
    e.preventDefault();
    const next = e.key === 'ArrowRight'
      ? Math.min(chips.length - 1, current + 1)
      : Math.max(0, current - 1);
    chips[next]?.focus();
  }, []);

  if (threads.length === 0) {
    // Nothing to show yet (should be rare — the hook bootstraps a default
    // thread on first hydrate). Still render the "+" so users can create
    // their first project.
    return (
      <>
        <ThreadNewButton
          type="button"
          onClick={onNew}
          aria-label="start new thread"
          title="new thread"
        >
          +
        </ThreadNewButton>
      </>
    );
  }

  // History note (kept for context):
  //   Earlier revisions of this component short-circuited the chip
  //   scroller when there was a single empty "new thread" (default
  //   title, messageCount === 0) — first by returning null entirely,
  //   then by collapsing to just the "+" button. Both variants caused
  //   bugs:
  //     • The null-return hid the only way to create a new thread
  //       (Miles flagged this on /justiceisurright thread
  //       1778281880.499349 after the opt-in landed but no "+"
  //       appeared).
  //     • The collapse-to-"+" variant resurfaced as Issue #84:
  //       "Clear Chat History wipes the chip rail from the DOM despite
  //       intact localStorage." After confirmClearChatWithReset runs,
  //       the active thread is renamed to the localized default and
  //       the live messages array drains to []. That puts the lone
  //       remaining thread in exactly the (default-title, 0-messages)
  //       state the short-circuit was suppressing — so the chip
  //       vanished while the user watched, even though localStorage
  //       still held the thread record. A page reload restored it,
  //       confirming the React rendering layer was the culprit.
  //   Fix: render the single-empty-default chip normally. The chip
  //   scroll container always emits the "+" button alongside, so the
  //   original /justiceisurright concern ("+" was hidden) is still
  //   addressed by the normal render path below — there is no longer
  //   any need for a separate empty-default branch.
  // Issue-#84-clear-history-chiprail-fix

  // T1 (thread-workflow UX): stable, user-controlled ordering — pinned first
  // (pin order), then unpinned by message activity. Deliberately NOT
  // active-first: the active thread is marked by STYLE, never by position, so
  // switching never reshuffles the strip and spatial memory holds.
  const ordered = orderThreads(threads);

  return (
    <>
      {overflow.left > 0 && (
        <ThreadOverflowIndicator
          type="button"
          data-thread-overflow="left"
          $side="left"
          $hot={overflow.leftHot}
          aria-label={`${overflow.left} more thread${overflow.left === 1 ? '' : 's'} to the left${overflow.leftHot ? ', with activity' : ''}`}
          title="scroll to earlier threads"
          onClick={() => pageScroll(-1)}
        >
          ‹ {overflow.left}
        </ThreadOverflowIndicator>
      )}
      <ThreadSwitcherScroll
        ref={scrollRef}
        role="tablist"
        aria-orientation="horizontal"
        /* Which edge fades (2026-09-20) — the SAME overflow numbers the
           indicators above are rendered from, so the fade and the control can
           never disagree about which side has more. Absent on a side with
           nothing hidden, so a short rail shows its first and last chip at
           full strength rather than permanently dimmed. */
        data-overflow-left={overflow.left > 0 ? '' : undefined}
        data-overflow-right={overflow.right > 0 ? '' : undefined}
        onWheel={handleWheel}
        onKeyDown={handleTablistKeyDown}
        onScroll={hideChipTooltip}
      >
        {ordered.map(thread => {
          const isActive = thread.id === activeThreadId;
          const isArchiveInFlight = archiveInFlightIds.has(thread.id);
          // UNIFORM in-summary activity indicator (owner directive, Miles
          // 2026-07-14 — supersedes the 2026-07-12 "pane-owns-active" rule):
          // each chip's indicator is driven by its OWN id in the per-thread
          // processing map, for EVERY thread — active included — so a
          // processing thread's summary is clear and consistent whether or
          // not the user has it open. Thread-isolation still holds: thread
          // A's stream never spins thread B's chip. One code path — the
          // spinner + elapsed clock component — so a chip can never
          // double-spin. The pane's own widgets are content, not rail state,
          // and are unchanged.
          //
          // OWNER OVERRIDE (Miles 2026-08-01): the active chip shows the
          // indicator even while the active thread's pane pending-overlay is
          // visible — the old `activePaneOverlayVisible` suppression
          // (2026-07-15 "pane-owns-active clock" rule) is deliberately GONE.
          // The chip is the at-a-glance status surface in every view/mode,
          // so the pane clock and the chip clock coexist by design.
          const isTurnRunning = !!(processingByThread && processingByThread[thread.id]);
          const showTurnIndicator = isTurnRunning;
          const turnSinceMs = showTurnIndicator
            ? (turnStartedAtByThread?.[thread.id] ?? null)
            : null;
          // Per-thread badges (2026-09-08): a COUNT when this device counted
          // arrivals (markUnread, from the background watcher), a DOT when
          // only the server-driven predicate says someone else was here.
          // Phantom-proof count (2026-09-08): zero when the viewer has viewed the
          // thread since its last activity, whatever a stale counter stored.
          const unread = effectiveUnreadCount(thread);
          // PR-3 (2026-09-08): the OPEN thread keeps its badge until the viewer has
          // dwelt on it for 3 s (markThreadViewed) — opening is not reading.
          const showBadge = unread > 0;
          const showDot = !showBadge && hasUnseenActivity(thread, activeThreadId, viewerUid);
          const displayCount = unread > 99 ? '99+' : String(unread);
          // Naming coherence (2026-08-01): routine chips are called by the
          // routine's NAME (title fallback) — same shared rule as the
          // sidebar rows / header / manager (routineThreadDisplayName).
          // PR-5 (2026-09-09): chat chips are called by the one display-title
          // rule (a unified rename > the other person in a direct message >
          // the record's own title) — the same name the rail row shows.
          const displayTitle = thread.routineId
            ? routineThreadDisplayName(thread)
            : threadDisplayTitleFor(thread, listIdentity, viewerUid);
          const chipKind = thread.routineId
            ? null
            : threadDisplayKindFor(thread, listIdentity, viewerUid, participantsByThread);
          // Hover/focus tooltip. Falls back to the chip name itself when
          // we don't yet have an inferred topic summary (first render
          // before inferThreadLabel resolves) so the popover is never empty.
          const tooltipText = (thread.topicSummary && thread.topicSummary.trim())
            ? thread.topicSummary
            : displayTitle;
          // Navigability (2026-07-13): the tooltip carries the same
          // relative-time + message-count meta the sidebar rows show, so
          // compact views are equally scannable. Routine threads
          // (2026-07-31) additionally surface their schedule summary here —
          // the compact strip has no room for a meta line on the chip itself.
          const tooltipMeta = [
            relativeActivityLabel(thread.lastActiveAt),
            thread.messageCount > 0
              ? `${thread.messageCount} message${thread.messageCount === 1 ? '' : 's'}`
              : 'empty',
            thread.routineId && thread.routineScheduleSummary
              ? `Routine · ${thread.routineScheduleSummary}`
              : '',
          ].filter(Boolean).join(' · ');
          return (
            <ThreadChipButton
              key={thread.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={
                `${displayTitle}${isTurnRunning ? ' (working)' : ''}` +
                (showBadge ? `, ${unread} unread` : showDot ? ', new activity' : '')
              }
              // No native `title`: the body-portaled tooltip now actually
              // renders on hover/focus in every mode (docked-UX round
              // 2026-07-13), and a native title on top of it stacked TWO
              // tooltips (adversarial review). Screen readers get the full
              // content via aria-label above; the portal covers sighted
              // hover/focus.
              data-thread-id={thread.id}
              data-completed-pulse={pulsingIds.has(thread.id) ? 'true' : 'false'}
              $active={isActive}
              aria-disabled={isArchiveInFlight}
              onClick={() => {
                if (isArchiveInFlight) return;
                handleChipClick(thread.id);
              }}
              // Hover/focus reveal for the body-portaled tooltip below —
              // onFocus/onBlur bubble in React, so focusing the chip's pin/×
              // children keeps the tooltip up (old :focus-within behavior).
              onMouseEnter={(e) => showChipTooltip(e.currentTarget, thread.id, tooltipText, tooltipMeta)}
              onMouseLeave={hideChipTooltip}
              onFocus={(e) => showChipTooltip(e.currentTarget, thread.id, tooltipText, tooltipMeta)}
              onBlur={hideChipTooltip}
            >
              {/* Routine Threads (2026-07-31): the glyph marks routine-thread
                  chips ahead of the label — gold while active, muted gray
                  while paused. Pure marker; chip behavior is unchanged. */}
              {!!thread.routineId && (
                <RoutineGlyph active={!isRoutineStopped(thread.routineStatus)} size={11} />
              )}
              {/* Round 3 (2026-08-01): group-thread chips wear the two-people
                  glyph. v2 (2026-08-16): INDEPENDENT of routine-ness — a
                  routine group thread wears BOTH glyphs (the directive's
                  "group icon also indicated"). Individual chips deliberately
                  carry NO type glyph — the strip is compact and a bubble on
                  every chip read as clutter; the sidebar is the primary
                  type-marked surface. */}
              {/* PR-5: a direct-message chip wears the one-person glyph, a
                  group chip the two-people glyph; individual chips still none. */}
              {chipKind === 'direct' ? (
                <ThreadTypeGlyph kind="direct" active={isActive} size={11} />
              ) : isGroupParticipantCount(participantsByThread?.[thread.id] ?? 1) && (
                <ThreadTypeGlyph kind="group" active={isActive} size={11} />
              )}
              <ThreadChipLabel $active={isActive}>{displayTitle}</ThreadChipLabel>
              {showTurnIndicator && (
                <ThreadTurnIndicator sinceMs={turnSinceMs} />
              )}
              {showBadge && (
                <ThreadUnreadBadge aria-hidden="true" data-unread-count={unread} $placement="overhang">
                  {displayCount}
                </ThreadUnreadBadge>
              )}
              {showDot && (
                <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="overhang" />
              )}
              {onTogglePin && (
                <ThreadChipPinButton
                  type="button"
                  data-thread-pin-btn=""
                  data-pinned={thread.pinned === true ? 'true' : 'false'}
                  $pinned={thread.pinned === true}
                  tabIndex={-1}
                  aria-pressed={thread.pinned === true}
                  aria-label={thread.pinned ? `unpin thread ${thread.title}` : `pin thread ${thread.title} to the front`}
                  title={thread.pinned ? 'unpin thread' : 'pin thread to front'}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onTogglePin(thread.id);
                  }}
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true">
                    <path
                      d="M7.2 1.2 L10.8 4.8 L8.6 5.2 L6.4 7.4 L6 10.8 L3.8 8.6 L1.2 11.2 M3.8 8.6 L1.6 6.4 L5 6 L7.2 3.8 Z"
                      fill={thread.pinned ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ThreadChipPinButton>
              )}
              {ARCHIVE_ALWAYS_AVAILABLE && (
                <ThreadChipCloseButton
                  type="button"
                  data-thread-close-btn=""
                  tabIndex={-1}
                  aria-disabled={isArchiveInFlight}
                  disabled={isArchiveInFlight}
                  aria-label={`archive thread ${thread.title} (Prest0n keeps it in memory for reference; the conversation closes)`}
                  title="archive thread"
                  onClick={e => handleCloseClick(e, thread.id)}
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 3 L9 9 M9 3 L3 9" strokeLinecap="round" />
                  </svg>
                </ThreadChipCloseButton>
              )}
            </ThreadChipButton>
          );
        })}
      </ThreadSwitcherScroll>
      {overflow.right > 0 && (
        <ThreadOverflowIndicator
          type="button"
          data-thread-overflow="right"
          $side="right"
          $hot={overflow.rightHot}
          aria-label={`${overflow.right} more thread${overflow.right === 1 ? '' : 's'} to the right${overflow.rightHot ? ', with activity' : ''}`}
          title="scroll to more threads"
          onClick={() => pageScroll(1)}
        >
          {overflow.right} ›
        </ThreadOverflowIndicator>
      )}
      <ThreadNewButton
        type="button"
        onClick={onNew}
        aria-label="start new thread"
        title="new thread"
      >
        +
      </ThreadNewButton>
      {/* Body portal at BODY_PORTAL_Z_INDEX (see ThreadChipTooltip in
          styles.ts): escapes the rail's overflow clipping AND the docked
          pane's overflow:hidden, in every chat mode. */}
      {chipTooltip && typeof document !== 'undefined' && createPortal(
        <ThreadChipTooltip
          role="tooltip"
          aria-hidden="true"
          data-prest0n-portal="true"
          data-thread-tooltip=""
          $x={chipTooltip.x}
          $bottom={chipTooltip.bottom}
        >
          {chipTooltip.text}
          {chipTooltip.meta && (
            <ThreadChipTooltipMeta data-thread-tooltip-meta="">
              {chipTooltip.meta}
            </ThreadChipTooltipMeta>
          )}
        </ThreadChipTooltip>,
        document.body,
      )}
    </>
  );
};

export default ThreadSwitcherBar;
